import {
  Autocomplete,
  type AutocompleteProps,
  Button,
  Chip,
  type ChipProps,
  Grid,
  TextField
} from '@mui/material'
import React, { useState } from 'react'

export interface TagData {
  name: string
  values: string[]
}

export type TagValidationResult = {
  isValid: boolean
  errors: string[] | null
}

export interface AddTagsFormProps {
  isDisabled?: boolean
  onAdd: (tagData: TagData) => void
  tagNames: string[]
  tagValues: string[]
  getChipColor?: (tag: { name: string; value: string }) => ChipProps['color']
  validateTagName: (tagName: string, tagNames: string[]) => TagValidationResult
  validateTagValues: (tagValues: string[]) => TagValidationResult
}

type TagNameAutocompletePropsDefinition = AutocompleteProps<
  string,
  false,
  false,
  true
>

type TagValuesAutocompletePropsDefinition = AutocompleteProps<
  string,
  true,
  false,
  true
>

const TAG_VALUES_HELPER_TEXT =
  "Multiple values are supported ('Enter' key to add a value)"

export function TagValueChip({ label, ...rest }: ChipProps) {
  return <Chip label={label} {...rest} />
}

export function AddTagsForm({
  getChipColor,
  isDisabled = false,
  onAdd,
  tagNames,
  tagValues,
  validateTagName,
  validateTagValues
}: AddTagsFormProps) {
  const [tagNameInputValue, setTagNameInputValue] = useState('')
  const [tagValuesInputValue, setTagValuesInputValue] = useState('')
  const [tagValuesResultValue, setTagValuesResultValue] = useState<string[]>([])
  const [errorsTagName, setErrorTagName] = useState<null | string[]>(null)
  const [errorsTagValues, setErrorTagValues] = useState<null | string[]>(null)

  const handleTagNameInputChange: TagNameAutocompletePropsDefinition['onInputChange'] =
    (_, newInputValue) => {
      setTagNameInputValue(newInputValue)
      const { errors } = validateTagName(newInputValue, tagNames)
      setErrorTagName(errors)
    }

  const handleTagValuesInputChange: TagValuesAutocompletePropsDefinition['onInputChange'] =
    (_, newInputValue) => {
      setTagValuesInputValue(newInputValue)
      const { errors } = validateTagValues([newInputValue])
      setErrorTagValues(errors)
    }

  const handleTagValuesOnChange: TagValuesAutocompletePropsDefinition['onChange'] =
    (_, newValues) => {
      setTagValuesResultValue(newValues)
      const { errors } = validateTagValues(newValues)
      setErrorTagValues(errors)
    }

  const handleTagValuesInputBlur: TagValuesAutocompletePropsDefinition['onBlur'] =
    () => {
      const allValues = [
        ...tagValuesResultValue,
        ...(tagValuesInputValue.length ? [tagValuesInputValue] : [])
      ]

      setTagValuesResultValue(allValues)
      setTagValuesInputValue('')
      const { errors } = validateTagValues(allValues)
      setErrorTagValues(errors)
    }

  const onSubmit = () => {
    const tagData = {
      name: tagNameInputValue,
      values: tagValuesResultValue
    }

    const tagNameValidationResult = validateTagName(tagData.name, tagNames)
    const tagValuesValidationResult = validateTagValues(tagData.values)

    if (tagNameValidationResult.isValid && tagValuesValidationResult.isValid) {
      onAdd(tagData)
      setTagNameInputValue('')
      setTagValuesResultValue([])
    }

    setErrorTagName(tagNameValidationResult.errors)
    setErrorTagValues(tagValuesValidationResult.errors)
  }

  return (
    <Grid container spacing={1}>
      <Grid item md={5} xs={6}>
        <Autocomplete<string, false, false, true>
          freeSolo
          disabled={isDisabled}
          getOptionDisabled={() => true}
          inputValue={tagNameInputValue}
          options={
            /* this is so it's easier to see which tags are already present */
            tagNames
          }
          renderInput={(params) => (
            <TextField
              {...params}
              data-testid="add-tags-form-tag-name-text-field"
              disabled={isDisabled}
              error={Boolean(errorsTagName)}
              helperText={errorsTagName ? errorsTagName.join('. ') : ''}
              label="Tag Name"
              name="serviceSettingsTagName"
            />
          )}
          onInputChange={handleTagNameInputChange}
        />
      </Grid>
      <Grid item md={5} xs={6}>
        <Autocomplete<string, true, false, true>
          freeSolo
          multiple
          disabled={isDisabled}
          inputValue={
            /* these are the characters input as the user types */
            tagValuesInputValue
          }
          limitTags={6}
          options={tagValues}
          renderInput={(params) => (
            <TextField
              {...params}
              data-testid="add-tags-form-tag-values-text-field"
              disabled={isDisabled}
              error={Boolean(errorsTagValues)}
              helperText={
                errorsTagValues
                  ? errorsTagValues.join('. ')
                  : TAG_VALUES_HELPER_TEXT
              }
              label="Tag Values"
              name="tagValues"
            />
          )}
          renderTags={(values, getTagProps) =>
            values.map((value, index) => {
              const { key, ...tagProps } = getTagProps({ index })

              return (
                <TagValueChip
                  key={key}
                  label={value}
                  {...tagProps}
                  {...(getChipColor && {
                    color: getChipColor({
                      name: tagNameInputValue,
                      value
                    })
                  })}
                />
              )
            })
          }
          value={
            /* The value after the user types 'Enter', clicks a value, or onBlur */
            tagValuesResultValue
          }
          onBlur={handleTagValuesInputBlur}
          onChange={handleTagValuesOnChange}
          onInputChange={handleTagValuesInputChange}
        />
      </Grid>
      <Grid item md={2} xs={2}>
        <Button
          data-testid="add-tags-form-submit-button"
          disabled={Boolean(errorsTagName || errorsTagValues) || isDisabled}
          variant="contained"
          onClick={onSubmit}
        >
          Add Tag
        </Button>
      </Grid>
    </Grid>
  )
}
